import { type DragEvent, useCallback, useEffect, useRef, useState } from 'react';

export type UploadStatus = 'idle' | 'preview' | 'uploading' | 'success' | 'error';

type UseFileUploadOptions = {
  onSuccess?: () => void;
  resetOnSuccess?: boolean;
  successDelayMs?: number;
};

const DEFAULT_SUCCESS_DELAY_MS = 1500;

export function useFileUpload(options: UseFileUploadOptions = {}) {
  const { onSuccess, resetOnSuccess, successDelayMs = DEFAULT_SUCCESS_DELAY_MS } = options;

  const [file, setFile] = useState<File>();
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [progress, setProgress] = useState(0);
  const [isInDropArea, setIsInDropArea] = useState(false);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearProgress = useCallback(() => {
    if (progressRef.current) {
      clearInterval(progressRef.current);
      progressRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    clearProgress();
    setFile(undefined);
    setUploadStatus('idle');
    setErrorMessage('');
    setProgress(0);
    setIsInDropArea(false);
  }, [clearProgress]);

  useEffect(() => {
    if (uploadStatus === 'success') {
      const timer = setTimeout(() => {
        if (resetOnSuccess) reset();
        onSuccess?.();
      }, successDelayMs);
      return () => clearTimeout(timer);
    }
  }, [uploadStatus, onSuccess, resetOnSuccess, reset, successDelayMs]);

  useEffect(() => {
    return () => {
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, []);

  const startUpload = useCallback(() => {
    clearProgress();
    setUploadStatus('uploading');
    setProgress(50);
    progressRef.current = setInterval(() => {
      setProgress((prev) => Math.min(prev + 5, 90));
    }, 150);
  }, [clearProgress]);

  const finishUpload = useCallback(() => {
    clearProgress();
    setProgress(100);
    setUploadStatus('success');
  }, [clearProgress]);

  const failUpload = useCallback(
    (message: string) => {
      clearProgress();
      setUploadStatus('error');
      setErrorMessage(message);
    },
    [clearProgress]
  );

  const setValidationError = useCallback((selectedFile: File, message: string) => {
    setFile(selectedFile);
    setUploadStatus('error');
    setErrorMessage(message);
  }, []);

  const handleDragEnter = useCallback(() => setIsInDropArea(true), []);
  const handleDragLeave = useCallback(() => setIsInDropArea(false), []);
  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsInDropArea(true);
  }, []);

  return {
    file,
    setFile,
    uploadStatus,
    setUploadStatus,
    progress,
    errorMessage,
    isInDropArea,
    setIsInDropArea,
    reset,
    startUpload,
    finishUpload,
    failUpload,
    setValidationError,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
  };
}
